import React from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineDocumentText,
  HiOutlineUser,
  HiOutlineSparkles,
  HiOutlineCheckCircle,
} from "react-icons/hi";

const AiImportHowItWorks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 lg:p-8 mb-6">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-blue-50 text-blue-600 mb-3 sm:mb-4">
          <HiOutlineSparkles className="w-6 h-6 sm:w-7 sm:h-7" />
        </div>
        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 leading-tight">
          {t("dashboard.aiConfig.import.howTitle")}
        </h2>
        <p className="text-xs sm:text-sm text-gray-600 mt-2">
          {t("dashboard.aiConfig.import.howSubtitle")}
        </p>
      </div>

      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-6xl mx-auto">
        <li className="relative bg-gray-50 border border-gray-200 rounded-xl p-4 pt-6 min-w-0 sm:col-span-2 lg:col-span-1">
          <span className="absolute -top-3 left-4 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm">
            1
          </span>
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <HiOutlineDocumentText size={22} className="flex-shrink-0" />
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">
              {t("dashboard.aiConfig.import.step1Title")}
            </h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed break-words">
            {t("dashboard.aiConfig.import.step1Body")}
          </p>
          <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              {t("dashboard.aiConfig.import.mockDocType")}
            </div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-mono">
                {t("dashboard.aiConfig.import.mockField1")}
              </span>
              <span className="flex-1 border-b border-dashed border-gray-200 min-w-[12px]" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-mono">
                {t("dashboard.aiConfig.import.mockField2")}
              </span>
              <span className="flex-1 border-b border-dashed border-gray-200 min-w-[12px]" />
            </div>
          </div>
        </li>

        <li className="relative bg-gray-50 border border-gray-200 rounded-xl p-4 pt-6 min-w-0">
          <span className="absolute -top-3 left-4 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm">
            2
          </span>
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <HiOutlineUser size={22} className="flex-shrink-0" />
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">
              {t("dashboard.aiConfig.import.step2Title")}
            </h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed break-words">
            {t("dashboard.aiConfig.import.step2Body")}
          </p>
          <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0 mb-2.5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                A
              </div>
              <div className="min-w-0 flex-1">
                <div className="h-1.5 bg-gray-200 rounded w-1/2 mb-1" />
                <div className="h-1 bg-gray-100 rounded w-3/4" />
              </div>
            </div>
            <div className="px-2 py-1 rounded bg-blue-50 border border-blue-100 text-xs text-blue-700 font-medium inline-flex items-center gap-1 max-w-full">
              <HiOutlineDocumentText size={13} className="flex-shrink-0" />
              <span className="break-words min-w-0">
                {t("dashboard.aiConfig.import.mockAttached")}
              </span>
            </div>
          </div>
        </li>

        <li className="relative bg-gray-50 border border-gray-200 rounded-xl p-4 pt-6 min-w-0">
          <span className="absolute -top-3 left-4 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm">
            3
          </span>
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <HiOutlineSparkles size={22} className="flex-shrink-0" />
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">
              {t("dashboard.aiConfig.import.step3Title")}
            </h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed break-words">
            {t("dashboard.aiConfig.import.step3Body")}
          </p>
          <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="h-1.5 bg-green-200 rounded w-1/3" />
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-semibold flex-shrink-0">
                <HiOutlineCheckCircle size={12} />
                {t("dashboard.aiConfig.import.mockFilled")}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded w-full mb-1.5" />
            <div className="h-1.5 bg-gray-200 rounded w-5/6" />
          </div>
        </li>
      </ol>
    </div>
  );
};

export default AiImportHowItWorks;
